import { supabase } from '../config/supabaseClient';
import localSampleDb from '../../../json/taxi-sample-db.json';
import localEnglishLanguage from '../../../json/language-en.json';

const privateBuckets = new Set(['cars', 'profiles', 'users', 'bookings']);
const singletonCollections = new Set(['settings', 'cancel_reason', 'smsConfig', 'smtpdata']);
let localSampleDbPromise = null;
let localEnglishLanguagePromise = null;

const requireClient = () => {
    if (!supabase) {
        throw new Error('Supabase is not configured. Set the platform Supabase URL and anon key.');
    }
    return supabase;
};

const throwIfError = ({ error }) => {
    if (error) {
        throw error;
    }
};

const readLocalSampleDb = async () => {
    if (typeof document === 'undefined') return localSampleDb;
    if (!localSampleDbPromise) {
        localSampleDbPromise = fetch('/sample-db.json')
            .then((response) => response.ok ? response.json() : null)
            .catch(() => localSampleDb);
    }
    return (await localSampleDbPromise) || localSampleDb;
};

const readLocalEnglishLanguage = async () => {
    if (typeof document === 'undefined') return localEnglishLanguage;
    if (!localEnglishLanguagePromise) {
        localEnglishLanguagePromise = fetch('/language-en.json')
            .then((response) => response.ok ? response.json() : null)
            .catch(() => localEnglishLanguage);
    }
    return (await localEnglishLanguagePromise) || localEnglishLanguage;
};

const withLocalLanguagePairs = async (collection, record) => {
    if (collection !== 'languages' || !record || record.keyValuePairs) return record;
    if (record.langLocale !== 'en' && !record.default) return record;
    const keyValuePairs = await readLocalEnglishLanguage();
    return keyValuePairs ? { ...record, keyValuePairs } : record;
};

const getLocalRecord = async (collection, recordId) => {
    const db = await readLocalSampleDb();
    const value = db?.[collection];
    if (value === undefined) return null;
    if (singletonCollections.has(collection) || Array.isArray(value) || value === null || typeof value !== 'object') {
        return String(recordId) === 'main' ? value : null;
    }
    return withLocalLanguagePairs(collection, value[String(recordId)] ?? null);
};

const listLocalRecords = async (collection, { filters = {}, limit } = {}) => {
    const db = await readLocalSampleDb();
    const value = db?.[collection];
    if (!value || Array.isArray(value) || typeof value !== 'object' || singletonCollections.has(collection)) {
        return {};
    }
    let entries = Object.entries(value).filter(([, record]) => (
        Object.entries(filters).every(([field, expected]) => String(record?.[field]) === String(expected))
    ));
    if (limit) entries = entries.slice(0, limit);
    const enrichedEntries = await Promise.all(entries.map(async ([id, record]) => (
        [id, await withLocalLanguagePairs(collection, record)]
    )));
    return Object.fromEntries(enrichedEntries);
};

export const resolveStorageReferences = async (value) => {
    if (typeof value === 'string' && value.startsWith('storage://')) {
        const [, , bucket, ...path] = value.split('/');
        const { data, error } = await requireClient().storage.from(bucket).createSignedUrl(path.join('/'), 3600);
        if (error) throw error;
        return data.signedUrl;
    }
    if (Array.isArray(value)) {
        return Promise.all(value.map(resolveStorageReferences));
    }
    if (value && typeof value === 'object') {
        const resolved = await Promise.all(Object.entries(value).map(async ([key, entry]) => (
            [key, await resolveStorageReferences(entry)]
        )));
        return Object.fromEntries(resolved);
    }
    return value;
};

const normalizeStorageReferences = (value) => {
    if (typeof value === 'string' && /^https?:\/\//i.test(value)) {
        try {
            const url = new URL(value);
            const signedPath = url.pathname.match(/\/storage\/v1\/object\/sign\/([^/]+)\/(.+)$/);
            return signedPath
                ? `storage://${signedPath[1]}/${decodeURIComponent(signedPath[2])}`
                : value;
        } catch {
            return value;
        }
    }
    if (Array.isArray(value)) return value.map(normalizeStorageReferences);
    if (value && typeof value === 'object') {
        return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, normalizeStorageReferences(entry)]));
    }
    return value;
};

export const getRecord = async (collection, recordId) => {
    try {
        const { data, error } = await requireClient()
            .from('app_records')
            .select('data')
            .eq('collection', collection)
            .eq('record_id', String(recordId))
            .maybeSingle();
        throwIfError({ error });
        return data ? resolveStorageReferences(data.data) : null;
    } catch (error) {
        const localRecord = await getLocalRecord(collection, recordId);
        if (localRecord !== null) return localRecord;
        throw error;
    }
};

export const listRecords = async (collection, { filters = {}, orderBy, ascending = true, limit } = {}) => {
    try {
        let request = requireClient()
            .from('app_records')
            .select('record_id, data')
            .eq('collection', collection);

        Object.entries(filters).forEach(([field, value]) => {
            request = request.eq(`data->>${field}`, String(value));
        });

        if (orderBy) {
            request = request.order(`data->>${orderBy}`, { ascending });
        }
        if (limit) {
            request = request.limit(limit);
        }

        const { data, error } = await request;
        throwIfError({ error });
        const resolved = await Promise.all((data || []).map(async (record) => (
            [record.record_id, await resolveStorageReferences(record.data)]
        )));
        return Object.fromEntries(resolved);
    } catch (error) {
        const localRecords = await listLocalRecords(collection, { filters, limit });
        if (Object.keys(localRecords).length) return localRecords;
        throw error;
    }
};

export const saveRecord = async (collection, recordId, value) => {
    const { data, error } = await requireClient().rpc('save_app_record', {
        p_collection: collection,
        p_record_id: String(recordId),
        p_data: normalizeStorageReferences(value),
    });
    throwIfError({ error });
    return data?.[0]?.saved_data ?? null;
};

export const deleteRecord = async (collection, recordId) => {
    const { error } = await requireClient()
        .from('app_records')
        .delete()
        .eq('collection', collection)
        .eq('record_id', String(recordId));
    throwIfError({ error });
};

export const uploadAppFile = async (bucket, filePath, file) => {
    let body = file;
    if (typeof file === 'string') {
        if (/^https?:\/\//i.test(file)) {
            try {
                const url = new URL(file);
                const signedPath = url.pathname.match(/\/storage\/v1\/object\/sign\/([^/]+)\/(.+)$/);
                if (signedPath) {
                    return `storage://${signedPath[1]}/${decodeURIComponent(signedPath[2])}`;
                }
            } catch {
                // Keep non-Supabase image URLs unchanged.
            }
            return file;
        }
        const response = await fetch(file);
        if (!response.ok) {
            throw new Error(`Unable to read selected file (${response.status}).`);
        }
        body = await response.blob();
    } else if (file?.uri) {
        const response = await fetch(file.uri);
        if (!response.ok) {
            throw new Error(`Unable to read selected file (${response.status}).`);
        }
        body = await response.blob();
    }

    const client = requireClient();
    const { error } = await client.storage.from(bucket).upload(filePath, body, {
        upsert: true,
        contentType: body?.type || file?.mimeType || undefined,
    });
    throwIfError({ error });

    if (privateBuckets.has(bucket)) {
        return `storage://${bucket}/${filePath}`;
    }
    const { data } = client.storage.from(bucket).getPublicUrl(filePath);
    return data.publicUrl;
};

export const deleteAppFile = async (bucket, filePath) => {
    const objectPath = filePath.startsWith('storage://')
        ? filePath.split('/').slice(3).join('/')
        : filePath;
    const { error } = await requireClient().storage.from(bucket).remove([objectPath]);
    throwIfError({ error });
};

export const subscribeToRecords = (collection, onChange) => {
    const client = requireClient();
    const channel = client
        .channel(`app-records:${collection}`)
        .on('postgres_changes', {
            event: '*',
            schema: 'public',
            table: 'app_records',
            filter: `collection=eq.${collection}`,
        }, onChange)
        .subscribe();

    return () => client.removeChannel(channel);
};

export const subscribeToRecord = (collection, recordId, onChange) => (
    subscribeToRecords(collection, async (change) => {
        const changedRecord = change.new || change.old;
        if (changedRecord?.record_id === String(recordId)) {
            onChange(change.eventType === 'DELETE' ? null : await resolveStorageReferences(changedRecord.data), change.eventType);
        }
    })
);

export const createRecordId = () => (
    `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`
);
